import { type FC, type ReactNode } from 'react';
import { css, styled } from 'styled-components';

import { colors } from 'src/css/theme';

import { DutchieButton } from '../dutchie-button';
import { DutchiePopup } from './popup';

const StickyFooter = css`
  margin-top: 0;
  position: sticky;
  bottom: 0;
  background-color: white;
  border-radius: 0 0 20px 20px;
`;

type ButtonsStyleProps = {
  reverse?: boolean;
  sticky?: boolean;
};

const Buttons = styled.div<ButtonsStyleProps>`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: ${({ reverse }) => (reverse ? 'row-reverse' : 'row')};
  justify-content: space-between;
  padding: 1rem 1.5rem;
  box-shadow: 0px -1px 0px ${colors.dutchie.shadowGrey};

  ${({ sticky }) => (sticky ? StickyFooter : null)}
`;

const CancelButton = styled.button`
  font-size: 1rem;
  color: ${colors.dutchie.almostBlack};
  text-decoration-line: underline;
  background-color: var(--color-grayscale-white);
  border: none;
  cursor: pointer;
`;

type ButtonProps = {
  disabled?: boolean;
  id?: string;
  onClick: () => void;
  text: ReactNode;
};

type ConfirmationPopupProps = {
  automationId?: string;
  buttonsSectionVisible?: boolean;
  cancel?: ButtonProps;
  canClose?: boolean;
  children?: ReactNode;
  className?: string;
  confirm?: ButtonProps;
  contentMaxHeight?: string;
  hide: () => void;
  isVisible: boolean;
  large?: boolean;
  medium?: boolean;
  portalContainer?: Element;
  small?: boolean;
  title: ReactNode;
  width?: string;
};

export const DutchieConfirmationPopup: FC<ConfirmationPopupProps> = ({
  buttonsSectionVisible = false,
  cancel,
  canClose,
  children,
  className,
  confirm,
  contentMaxHeight,
  hide,
  isVisible,
  title,
  ...rest
}) => (
  <DutchiePopup
    canClose={canClose}
    caption={title}
    className={className}
    contentMaxHeight={contentMaxHeight}
    hide={hide}
    isVisible={isVisible}
    {...rest}
  >
    {children}
    {(confirm ?? cancel ?? buttonsSectionVisible) && (
      <Buttons reverse={confirm && !cancel} sticky={contentMaxHeight !== undefined}>
        {cancel && (
          <CancelButton
            data-testid='confirmation-popup_cancel-button_cancel'
            disabled={cancel.disabled}
            id={cancel.id}
            onClick={cancel.onClick}
            type='button'
          >
            {cancel.text ?? 'Cancel'}
          </CancelButton>
        )}
        {confirm && (
          <DutchieButton
            automationId='confirmation-popup_confirm-button_confirm'
            disabled={confirm.disabled}
            id={confirm.id}
            onClick={confirm.onClick}
            type='button'
          >
            {confirm.text ?? 'Confirm'}
          </DutchieButton>
        )}
      </Buttons>
    )}
  </DutchiePopup>
);
